import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Banknote, CheckCircle2 } from 'lucide-react'
import { supabase } from '../lib/supabase'

type Session={id:string;opening_amount:number;expected_amount:number}
type Payment={method:string;amount:number}
type Movement={movement_type:string;amount:number}
const money=new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'})
const n=(value:unknown)=>Number(value)||0

export function CashCloseReview({session,payments,counted,onReadyChange}:{session:Session;payments:Payment[];counted:string;onReadyChange:(ready:boolean)=>void}){
 const [movements,setMovements]=useState<Movement[]>([])
 const [loading,setLoading]=useState(true)
 const [error,setError]=useState('')
 const load=useCallback(async()=>{
   setLoading(true)
   setError('')
   const {data,error:issue}=await supabase.from('cash_movements').select('movement_type,amount').eq('cash_session_id',session.id)
   if(issue){setError(issue.message);setMovements([])}
   else setMovements((data||[]) as Movement[])
   setLoading(false)
 },[session.id])
 useEffect(()=>{
   void load()
   const channel=supabase.channel('cash-close:'+session.id)
     .on('postgres_changes',{event:'*',schema:'public',table:'cash_movements',filter:'cash_session_id=eq.'+session.id},()=>void load())
     .on('postgres_changes',{event:'*',schema:'public',table:'payments',filter:'cash_session_id=eq.'+session.id},()=>void load())
     .subscribe()
   return()=>{void supabase.removeChannel(channel)}
 },[load,session.id])
 const totals=useMemo(()=>{
   const sum=(method:string)=>payments.filter(p=>p.method===method).reduce((total,p)=>total+n(p.amount),0)
   const supply=movements.filter(m=>m.movement_type==='supply').reduce((total,m)=>total+n(m.amount),0)
   const withdrawal=movements.filter(m=>m.movement_type==='withdrawal').reduce((total,m)=>total+n(m.amount),0)
   return {cash:sum('cash'),pix:sum('pix'),debit:sum('debit_card'),credit:sum('credit_card'),supply,withdrawal,
     expected:n(session.opening_amount)+sum('cash')+supply-withdrawal}
 },[movements,payments,session.opening_amount])
 const parsed=counted.trim()?Number(counted.replace(',','.')):NaN
 const countedValid=Number.isFinite(parsed)&&parsed>=0
 const discrepancy=countedValid?Math.round((parsed-totals.expected)*100)/100:null
 const ready=!loading&&!error&&countedValid
 useEffect(()=>{onReadyChange(ready)},[onReadyChange,ready])
 return <div className="cash2-close-review">
   <div className="cash2-close-title"><Banknote size={19}/><strong>Conferência de fechamento</strong></div>
   <div className="cash2-close-grid">
     <div><small>Abertura</small><strong>{money.format(n(session.opening_amount))}</strong></div>
     <div><small>Vendas em dinheiro</small><strong>{money.format(totals.cash)}</strong></div>
     <div><small>Suprimentos</small><strong>{loading?'…':money.format(totals.supply)}</strong></div>
     <div><small>Sangrias</small><strong>{loading?'…':'− '+money.format(totals.withdrawal)}</strong></div>
     <div className="cash2-close-key"><small>Esperado na gaveta</small><strong>{loading||error?'—':money.format(totals.expected)}</strong></div>
     <div className="cash2-close-key"><small>Dinheiro contado</small><strong>{countedValid?money.format(parsed):'Digite abaixo'}</strong></div>
   </div>
   <div className="cash2-close-methods">Recebido no turno: <strong>Pix {money.format(totals.pix)}</strong><strong>Débito {money.format(totals.debit)}</strong><strong>Crédito {money.format(totals.credit)}</strong></div>
   {error?<div className="form-error" role="alert">Conferência indisponível: {error}. Não feche o caixa sem revisar as movimentações.</div>:loading?<p>Conferindo movimentos financeiros…</p>:discrepancy!==null?<div className={'cash2-difference '+(Math.abs(discrepancy)>.009?'has-difference':'is-balanced')}>
     {Math.abs(discrepancy)>.009?<AlertTriangle size={18}/>:<CheckCircle2 size={18}/>}
     <strong>{Math.abs(discrepancy)>.009?(discrepancy>0?'Sobra de ':'Falta de ')+money.format(Math.abs(discrepancy)):'Dinheiro conferido sem diferença'}</strong>
   </div>:null}
   <small>Pix e cartões não entram no dinheiro físico. A conferência não movimenta valores; somente o botão de confirmação fecha o caixa.</small>
 </div>
}
